import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ACCION, aPesos } from "@zociety/core";
import { registrarAccion } from "@/lib/analitica";
import type { LineaCarritoApi } from "@/lib/api";

export interface ItemCarrito extends LineaCarritoApi {
  nombre: string;
  precio_centavos: number;
  imagen?: string;
}

const CLAVE = "zociety:carrito";

interface EstadoCarrito {
  items: ItemCarrito[];
  cantidadTotal: number;
  subtotalCentavos: number;
  agregar: (item: Omit<ItemCarrito, "cantidad">, cantidad?: number) => void;
  quitar: (itemId: string) => void;
  cambiarCantidad: (itemId: string, cantidad: number) => void;
  vaciar: () => void;
  abierto: boolean;
  setAbierto: (v: boolean) => void;
  aLineas: () => LineaCarritoApi[];
}

const Ctx = createContext<EstadoCarrito | null>(null);

export function CarritoProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ItemCarrito[]>(() => {
    try {
      const guardado = localStorage.getItem(CLAVE);
      return guardado ? (JSON.parse(guardado) as ItemCarrito[]) : [];
    } catch {
      return [];
    }
  });
  const [abierto, setAbierto] = useState(false);

  useEffect(() => {
    localStorage.setItem(CLAVE, JSON.stringify(items));
  }, [items]);

  const agregar = useCallback((item: Omit<ItemCarrito, "cantidad">, cantidad = 1) => {
    /*
     * Fuera del `setItems`, que React puede invocar dos veces en desarrollo.
     *
     * El actualizador de estado debe ser puro; emitir desde dentro contaría cada
     * añadido dos veces con el modo estricto activado, y esa duplicación no se
     * vería hasta mirar el reporte.
     */
    registrarAccion(
      ACCION.AGREGAR_AL_CARRITO,
      { currency: "MXN", value: aPesos(item.precio_centavos * cantidad) },
      [
        {
          item_id: item.item_id,
          item_name: item.nombre,
          price: aPesos(item.precio_centavos),
          quantity: cantidad,
        },
      ]
    );

    setItems((prev) => {
      // Los eventos de cumpleaños llevan fecha y hora propias: cada uno es una
      // línea distinta aunque compartan el mismo paquete.
      if (item.bday_config) return [...prev, { ...item, cantidad }];

      const existente = prev.find((i) => i.item_id === item.item_id && !i.bday_config);
      if (existente) {
        return prev.map((i) =>
          i.item_id === item.item_id && !i.bday_config ? { ...i, cantidad: i.cantidad + cantidad } : i
        );
      }
      return [...prev, { ...item, cantidad }];
    });
    setAbierto(true);
  }, []);

  const quitar = useCallback(
    (itemId: string) => {
      /* Se lee de `items` y no dentro del actualizador, por lo mismo que en
         `agregar`: el actualizador tiene que ser puro. */
      const fuera = items.find((i) => i.item_id === itemId);
      if (fuera) {
        registrarAccion(
          ACCION.QUITAR_DEL_CARRITO,
          { currency: "MXN", value: aPesos(fuera.precio_centavos * fuera.cantidad) },
          [{ item_id: fuera.item_id, item_name: fuera.nombre, quantity: fuera.cantidad }]
        );
      }
      setItems((prev) => prev.filter((i) => i.item_id !== itemId));
    },
    [items]
  );

  const cambiarCantidad = useCallback((itemId: string, cantidad: number) => {
    setItems((prev) =>
      cantidad <= 0
        ? prev.filter((i) => i.item_id !== itemId)
        : prev.map((i) => (i.item_id === itemId ? { ...i, cantidad } : i))
    );
  }, []);

  const valor = useMemo<EstadoCarrito>(
    () => ({
      items,
      cantidadTotal: items.reduce((n, i) => n + i.cantidad, 0),
      subtotalCentavos: items.reduce((n, i) => n + i.precio_centavos * i.cantidad, 0),
      agregar,
      quitar,
      cambiarCantidad,
      vaciar: () => setItems([]),
      abierto,
      setAbierto,
      aLineas: () =>
        items.map((i) => ({
          tipo: i.tipo,
          item_id: i.item_id,
          cantidad: i.cantidad,
          ...(i.bday_config ? { bday_config: i.bday_config } : {}),
        })),
    }),
    [items, abierto, agregar, quitar, cambiarCantidad]
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useCarrito() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCarrito debe usarse dentro de CarritoProvider");
  return ctx;
}
